import React from 'react';
import {
    openOrFocusTicketTab,
    setVisibleTicketFilters,
    setVisibleTickets,
} from '../../../../../actions/ui';
import {
    getDescriptionPreview,
    priorityOrderMap,
} from '../helpers';
import { useTaskListAssignee } from './useTaskListAssignee';
import { useTaskListDashboardStats } from './useTaskListDashboardStats';
import { useTaskListFiltersData } from './useTaskListFiltersData';
import { useTaskListStatusModal } from './useTaskListStatusModal';

export const useTaskListScreen = ({ token, dispatch, navigate, ticketCreatedAt }) => {
    const {
        tickets,
        setTickets,
        isLoading,
        error,
        setError,
        searchField,
        setSearchField,
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,
        statusGroupFilter,
        setStatusGroupFilter,
        typeFilter,
        setTypeFilter,
        customerFilter,
        setCustomerFilter,
        projectFilter,
        setProjectFilter,
        customerOptions,
        projectOptions,
        limit,
        setLimit,
        offset,
        setOffset,
        pagination,
        loadTickets,
        handleClearFilters,
        isDashboardCardActive,
        handleDashboardCardFilter,
    } = useTaskListFiltersData({ token });

    const { dashboardStats, loadDashboardStats } = useTaskListDashboardStats({ token });

    const [sortBy, setSortBy] = React.useState('createdDate');
    const [sortDirection, setSortDirection] = React.useState('desc');
    const [hoveredPreview, setHoveredPreview] = React.useState(null);
    const [selectedTicketIds, setSelectedTicketIds] = React.useState([]);

    const handleOpenTicketDetail = React.useCallback((ticket) => {
        if (!ticket?._id) {
            return;
        }

        dispatch(openOrFocusTicketTab({
            id: ticket._id,
            code: ticket.code,
            title: ticket.title,
        }));

        navigate(`/tasks/detail/${ticket._id}`, {
            state: { ticketCode: ticket.code },
        });
    }, [dispatch, navigate]);

    const getComparableValue = React.useCallback((ticket, field) => {
        switch (field) {
        case 'code':
            return String(ticket?.code || '').toUpperCase();
        case 'title':
            return String(ticket?.title || '').toUpperCase();
        case 'customer':
            return String(ticket?.customer?.name || '').toUpperCase();
        case 'project':
            return String(ticket?.project?.name || '').toUpperCase();
        case 'assignee':
            return String(ticket?.assignee?.name || ticket?.assignee?.email || '').toUpperCase();
        case 'priority':
            return priorityOrderMap[String(ticket?.priority || '').toUpperCase()] || 0;
        case 'type':
            return String(ticket?.type || '').toUpperCase();
        case 'status':
            return String(ticket?.status || '').toUpperCase();
        case 'dueDate': {
            const dueDate = ticket?.resolutionDueDate || ticket?.firstResponseDueDate;
            return dueDate ? new Date(dueDate).getTime() : 0;
        }
        case 'createdDate':
            return ticket?.createdDate ? new Date(ticket.createdDate).getTime() : 0;
        default:
            return '';
        }
    }, []);

    const sortedTickets = React.useMemo(() => {
        const rows = [...tickets];

        rows.sort((a, b) => {
            const valueA = getComparableValue(a, sortBy);
            const valueB = getComparableValue(b, sortBy);

            if (valueA === valueB) {
                return 0;
            }

            const sortResult = valueA > valueB ? 1 : -1;
            return sortDirection === 'asc' ? sortResult : sortResult * -1;
        });

        return rows;
    }, [tickets, sortBy, sortDirection, getComparableValue]);

    const selectedVisibleCount = React.useMemo(() => {
        const visibleIds = new Set(sortedTickets.map((ticket) => String(ticket._id)));
        return selectedTicketIds.filter((ticketId) => visibleIds.has(String(ticketId))).length;
    }, [sortedTickets, selectedTicketIds]);

    const allVisibleSelected = sortedTickets.length > 0 && selectedVisibleCount === sortedTickets.length;

    const getSortIcon = React.useCallback((field) => {
        if (sortBy !== field) {
            return '↕';
        }

        return sortDirection === 'asc' ? '↑' : '↓';
    }, [sortBy, sortDirection]);

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortDirection((previous) => (previous === 'asc' ? 'desc' : 'asc'));
            return;
        }

        setSortBy(field);
        setSortDirection('asc');
    };

    const showDescriptionPreview = (ticket, targetElement) => {
        if (!targetElement) return;

        const rect = targetElement.getBoundingClientRect();
        const width = Math.min(360, Math.max(260, Math.floor(window.innerWidth * 0.8)));
        const estimatedHeight = 190;

        let left = rect.left;
        if (left + width > window.innerWidth - 12) {
            left = window.innerWidth - width - 12;
        }
        left = Math.max(12, left);

        let top = rect.bottom + 8;
        if (top + estimatedHeight > window.innerHeight - 12) {
            top = Math.max(12, rect.top - estimatedHeight - 8);
        }

        setHoveredPreview({
            top,
            left,
            width,
            description: getDescriptionPreview(ticket),
        });
    };

    const hideDescriptionPreview = () => {
        setHoveredPreview(null);
    };

    React.useEffect(() => {
        const handleViewportChange = () => {
            setHoveredPreview(null);
        };

        window.addEventListener('scroll', handleViewportChange, true);
        window.addEventListener('resize', handleViewportChange);

        return () => {
            window.removeEventListener('scroll', handleViewportChange, true);
            window.removeEventListener('resize', handleViewportChange);
        };
    }, []);

    React.useEffect(() => {
        loadDashboardStats();
    }, [loadDashboardStats]);

    React.useEffect(() => {
        const currentTicketIds = new Set(tickets.map((ticket) => String(ticket._id)));
        setSelectedTicketIds((previous) => previous.filter((ticketId) => currentTicketIds.has(String(ticketId))));
    }, [tickets]);

    const {
        updatingAssigneeIds,
        isAssigneeModalOpen,
        assigneeModalTicketId,
        assigneeSearchTerm,
        assigneeOptions,
        isAssigneeSearching,
        assigneeModalError,
        openAssigneeModal,
        closeAssigneeModal,
        setAssigneeSearchTerm,
        handleSelectAssigneeFromModal,
    } = useTaskListAssignee({
        token,
        setTickets,
        setError,
    });

    const {
        isStatusModalOpen,
        statusModalMode,
        statusModalTargetStatus,
        statusModalNote,
        statusModalIsPublic,
        statusModalAttachments,
        statusModalError,
        isSavingStatusModal,
        statusModalTitle,
        statusModalSubtitle,
        openStatusModalForBulk: openStatusModalForBulkInternal,
        handleStatusChangeIntent,
        setStatusModalTargetStatus,
        setStatusModalNote,
        setStatusModalIsPublic,
        setStatusModalAttachments,
        handleRemoveModalAttachment,
        closeStatusModal,
        handleSubmitStatusModal,
    } = useTaskListStatusModal({
        token,
        tickets,
        selectedTicketIds,
        setSelectedTicketIds,
        loadTickets,
        loadDashboardStats,
    });

    React.useEffect(() => {
        dispatch(setVisibleTickets(sortedTickets));
    }, [dispatch, sortedTickets]);

    React.useEffect(() => {
        dispatch(setVisibleTicketFilters({
            searchField,
            searchTerm,
            statusFilter,
            statusGroupFilter,
            typeFilter,
            customerFilter,
            projectFilter,
        }));
    }, [
        dispatch,
        searchField,
        searchTerm,
        statusFilter,
        statusGroupFilter,
        typeFilter,
        customerFilter,
        projectFilter,
    ]);

    React.useEffect(() => {
        if (!ticketCreatedAt) {
            return;
        }

        loadTickets();
        loadDashboardStats();
    }, [ticketCreatedAt, loadTickets, loadDashboardStats]);

    React.useEffect(() => {
        return () => {
            dispatch(setVisibleTickets([]));
        };
    }, [dispatch]);

    const handleToggleTicketSelection = (ticketId) => {
        setSelectedTicketIds((previous) => {
            const id = String(ticketId || '');
            if (!id) return previous;

            if (previous.some((item) => String(item) === id)) {
                return previous.filter((item) => String(item) !== id);
            }

            return [...previous, id];
        });
    };

    const handleToggleSelectAllVisible = (isChecked) => {
        if (!isChecked) {
            const visibleIds = new Set(sortedTickets.map((ticket) => String(ticket._id)));
            setSelectedTicketIds((previous) => previous.filter((ticketId) => !visibleIds.has(String(ticketId))));
            return;
        }

        const visibleIds = sortedTickets.map((ticket) => String(ticket._id));
        setSelectedTicketIds((previous) => {
            const merged = new Set(previous.map((ticketId) => String(ticketId)));
            visibleIds.forEach((ticketId) => merged.add(ticketId));
            return Array.from(merged);
        });
    };

    const openStatusModalForBulk = React.useCallback(() => {
        const result = openStatusModalForBulkInternal();
        if (!result?.success) {
            setError(result?.message || 'Selecciona al menos un ticket para aplicar cambios masivos.');
        }
    }, [openStatusModalForBulkInternal, setError]);

    const assigneeModalTicket = React.useMemo(() => {
        return tickets.find((ticket) => String(ticket._id) === String(assigneeModalTicketId));
    }, [tickets, assigneeModalTicketId]);

    return {
        tickets,
        isLoading,
        error,
        searchField,
        searchTerm,
        statusFilter,
        statusGroupFilter,
        typeFilter,
        customerFilter,
        projectFilter,
        customerOptions,
        projectOptions,
        limit,
        offset,
        pagination,
        dashboardStats,
        hoveredPreview,
        selectedTicketIds,
        updatingAssigneeIds,
        isStatusModalOpen,
        statusModalMode,
        statusModalTargetStatus,
        statusModalNote,
        statusModalIsPublic,
        statusModalAttachments,
        statusModalError,
        isSavingStatusModal,
        isAssigneeModalOpen,
        assigneeModalTicket,
        assigneeSearchTerm,
        assigneeOptions,
        isAssigneeSearching,
        assigneeModalError,
        sortedTickets,
        allVisibleSelected,
        statusModalTitle,
        statusModalSubtitle,
        getSortIcon,
        handleSort,
        showDescriptionPreview,
        hideDescriptionPreview,
        loadTickets,
        setSearchField,
        setSearchTerm,
        setStatusFilter,
        setStatusGroupFilter,
        setTypeFilter,
        setCustomerFilter,
        setProjectFilter,
        setLimit,
        setOffset,
        handleClearFilters,
        handleDashboardCardFilter,
        isDashboardCardActive,
        handleToggleTicketSelection,
        handleToggleSelectAllVisible,
        openStatusModalForBulk,
        handleStatusChangeIntent,
        handleOpenTicketDetail,
        openAssigneeModal,
        closeAssigneeModal,
        setAssigneeSearchTerm,
        handleSelectAssigneeFromModal,
        setStatusModalTargetStatus,
        setStatusModalNote,
        setStatusModalIsPublic,
        setStatusModalAttachments,
        handleRemoveModalAttachment,
        closeStatusModal,
        handleSubmitStatusModal,
        setSelectedTicketIds,
    };
};
